import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { API } from "../lib/api";

export const ContactPage = () => {
  const [formData, setFormData] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (err) { toast.error("Failed to send message. Please try again."); }
    setLoading(false);
  };

  const campuses = [
    { name: "Main Campus", location: "Takoradi", address: "P. O. Box 682, Takoradi", phone: "0552184037", color: "bg-gradient-to-br from-purple-600 to-violet-700" },
    { name: "Annex Campus", location: "Lagos Town, Takoradi", address: "P. O. Box 682, Lagos Town", phone: "0530017233", color: "bg-gradient-to-br from-blue-600 to-blue-700" }
  ];

  const faqs = [
    { q: "How do I enroll my child?", a: "Visit our Admissions page or contact either campus office." },
    { q: "What are the school hours?", a: "Regular school hours are 8:00 AM to 3:00 PM, Monday through Friday." },
    { q: "How can I access my child's grades?", a: "Log in to the Parent Portal using your registered email and password." },
    { q: "Is transportation provided?", a: "Yes, we offer bus services for students within the Takoradi area." },
  ];

  return (
    <div className="py-16 bg-gradient-to-b from-yellow-400 to-orange-400">
      <div className="max-w-7xl mx-auto px-4">
        <h1 className="text-4xl font-bold text-[#002147] mb-8 text-center">Contact Us</h1>
        <div className="grid lg:grid-cols-2 gap-12">
          <Card>
            <CardHeader>
              <CardTitle>Send us a Message</CardTitle>
              <CardDescription>Fill out the form below and we'll get back to you shortly.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label htmlFor="name">Name</Label><Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="contact-name" /></div>
                  <div><Label htmlFor="email">Email</Label><Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="contact-email" /></div>
                </div>
                <div><Label htmlFor="subject">Subject</Label><Input id="subject" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} required data-testid="contact-subject" /></div>
                <div><Label htmlFor="message">Message</Label><Textarea id="message" rows={5} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} required data-testid="contact-message" /></div>
                <Button type="submit" className="w-full bg-[#FF6B00] hover:bg-[#E65C00]" disabled={loading} data-testid="contact-submit">
                  {loading ? "Sending..." : "Send Message"} <Send size={16} className="ml-2" />
                </Button>
              </form>
            </CardContent>
          </Card>
          <div className="space-y-6">
            <div className="grid gap-4">
              {campuses.map((campus, index) => (
                <div key={index} className={`${campus.color} rounded-xl p-6 text-white`}>
                  <h3 className="text-xl font-bold mb-4">{campus.name}</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-3"><MapPin size={18} /> {campus.location}</div>
                    <div className="flex items-center gap-3"><Mail size={18} /> {campus.address}</div>
                    <div className="flex items-center gap-3"><Phone size={18} /> <a href={`tel:${campus.phone}`} className="underline">{campus.phone}</a></div>
                  </div>
                </div>
              ))}
            </div>
            <Card>
              <CardHeader><CardTitle>Frequently Asked Questions</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="border-b last:border-0 pb-4 last:pb-0">
                    <h4 className="font-semibold text-[#002147]">{faq.q}</h4>
                    <p className="text-gray-600 text-sm mt-1">{faq.a}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
